import React, { useState, useEffect } from 'react';
import { PlusIcon, MagnifyingGlassIcon, PencilIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';

interface Product {
  id: number;
  sku: string;
  name: string;
  description: string | null;
  category: string | null;
  unit_price: number;
  currency: string;
  quantity_in_stock: number;
  reorder_level: number;
  supplier_name: string | null;
  weight_kg: number | null;
  dimensions_cm: string | null;
  is_fragile: boolean;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
}

const MerchantProductInventory: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  
  // Form states
  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    description: '',
    category: '',
    unit_price: '',
    quantity_in_stock: '',
    reorder_level: '10',
    supplier_name: '',
    weight_kg: '',
    dimensions_cm: '',
    is_fragile: false
  });

  useEffect(() => {
    fetchProducts();
  }, [searchTerm]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/merchant/products', {
        params: { search: searchTerm || undefined }
      });
      setProducts(response.data.products);
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const formDataObj = new FormData();
      formDataObj.append('sku', formData.sku);
      formDataObj.append('name', formData.name);
      formDataObj.append('description', formData.description);
      formDataObj.append('category', formData.category);
      formDataObj.append('unit_price', formData.unit_price);
      formDataObj.append('quantity_in_stock', formData.quantity_in_stock);
      formDataObj.append('reorder_level', formData.reorder_level);
      formDataObj.append('supplier_name', formData.supplier_name);
      if (formData.weight_kg) formDataObj.append('weight_kg', formData.weight_kg);
      if (formData.dimensions_cm) formDataObj.append('dimensions_cm', formData.dimensions_cm);
      formDataObj.append('is_fragile', String(formData.is_fragile));

      await api.post('/merchant/products', formDataObj);
      setShowAddModal(false);
      fetchProducts();
      resetForm();
      alert('Product added successfully!');
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to add product');
    }
  };

  const handleUpdateStock = async (productId: number, newQuantity: number) => {
    try {
      const formDataObj = new FormData();
      formDataObj.append('quantity_in_stock', String(newQuantity));
      
      await api.put(`/merchant/products/${productId}`, formDataObj);
      fetchProducts();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to update stock');
    }
  };

  const resetForm = () => {
    setFormData({
      sku: '',
      name: '',
      description: '',
      category: '',
      unit_price: '',
      quantity_in_stock: '',
      reorder_level: '10',
      supplier_name: '',
      weight_kg: '',
      dimensions_cm: '',
      is_fragile: false
    });
  };

  const formatCurrency = (amount: number) => {
    return `₦${amount.toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
  };

  const getStockStatus = (product: Product) => {
    if (product.quantity_in_stock === 0) {
      return { text: 'Out of Stock', color: 'text-red-600 bg-red-100' };
    } else if (product.quantity_in_stock <= product.reorder_level) {
      return { text: 'Low Stock', color: 'text-yellow-600 bg-yellow-100' };
    }
    return { text: 'In Stock', color: 'text-green-600 bg-green-100' };
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 font-poppins">Product Inventory</h1>
            <p className="text-gray-600 font-poppins">Manage your products and stock levels</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Product
          </button>
        </div>

        {/* Search */}
        <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-4 mb-6">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by product name or SKU..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-volexx-blue-600 mx-auto"></div>
            </div>
          ) : products.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-500 font-poppins">No products found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {products.map((product) => {
                    const stockStatus = getStockStatus(product);
                    return (
                      <tr key={product.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900 font-poppins">{product.name}</div>
                          {product.description && (
                            <div className="text-sm text-gray-500 font-poppins">{product.description}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 font-poppins">{product.sku}</td>
                        <td className="px-6 py-4 text-sm text-gray-900 font-poppins">{product.category || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-900 font-poppins">{formatCurrency(product.unit_price)}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-900 font-poppins">{product.quantity_in_stock}</span>
                            <button
                              onClick={() => {
                                const newQty = prompt(`Update stock for ${product.name}:`, String(product.quantity_in_stock));
                                if (newQty !== null) {
                                  handleUpdateStock(product.id, parseInt(newQty));
                                }
                              }}
                              className="text-volexx-blue-600 hover:text-volexx-blue-800"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${stockStatus.color}`}>
                            {stockStatus.text}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          <button className="text-volexx-blue-600 hover:text-volexx-blue-800 font-poppins">
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Add Product Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-volexx-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Add New Product</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 font-poppins">SKU *</label>
                      <input
                        type="text"
                        required
                        value={formData.sku}
                        onChange={(e) => setFormData({...formData, sku: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 font-poppins">Product Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 font-poppins">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 font-poppins">Category</label>
                      <input
                        type="text"
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 font-poppins">Unit Price (₦) *</label>
                      <input
                        type="number"
                        required
                        step="0.01"
                        value={formData.unit_price}
                        onChange={(e) => setFormData({...formData, unit_price: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 font-poppins">Initial Stock</label>
                      <input
                        type="number"
                        value={formData.quantity_in_stock}
                        onChange={(e) => setFormData({...formData, quantity_in_stock: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 font-poppins">Reorder Level</label>
                      <input
                        type="number"
                        value={formData.reorder_level}
                        onChange={(e) => setFormData({...formData, reorder_level: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 font-poppins">Supplier Name</label>
                    <input
                      type="text"
                      value={formData.supplier_name}
                      onChange={(e) => setFormData({...formData, supplier_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_fragile}
                      onChange={(e) => setFormData({...formData, is_fragile: e.target.checked})}
                      className="h-4 w-4 text-volexx-blue-600 focus:ring-volexx-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900 font-poppins">
                      Fragile Item (Handle with care)
                    </label>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => { setShowAddModal(false); resetForm(); }}
                      className="px-4 py-2 border border-gray-300 rounded-volexx text-gray-700 hover:bg-gray-50 font-poppins"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                    >
                      Add Product
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MerchantProductInventory;
